import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, TextField, ToggleButton, ToggleButtonGroup,
  Box, IconButton, InputAdornment,
  TablePagination,
  TablePaginationActionsProps,
  useTheme,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  FormGroup,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Tooltip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { FullEvent } from '../shared/FullEvent';
import type {
  NumberRangeBaseFunction,
  NumberRangeDescription,
  NumberRangeSubFunction,
} from "../shared/NumberRange";
import EventsFormDialog from '../components/EventsFormDialog'
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useEventsApi } from "../hooks/useEventsApi";
import { getDescriptionByLanguage } from "../utils/eventUtils";
import { config } from "../config";
import CustomAlert from "../components/CustomAlert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { debounce } from "lodash";
import EventMenuButton from "../components/EventMenuButton";
import ExportMenu from "../components/ExportMenu";
import { alpha } from '@mui/material/styles';
import { EmtpyState } from "../components/EmptyState";
import getUserRoleFlags from '../hooks/useGetUserRoleFlags';
import AdminFilter from "../components/AdminFilter";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from "@mui/icons-material/Timeline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { EventLanguageCode } from "../constants/languages";
import type { SelectChangeEvent } from "@mui/material/Select";

/**
 * The rows get whatever the viewport has left over from the rest of the page: the sticky app
 * header, the page padding, the toolbar row, the closed filter panel, the pagination bar and
 * the app footer add up to the 420px. So with the filter panel closed the page fits the
 * viewport; opening it, or a window shorter than the 280px floor allows, scrolls the document.
 *
 * `dvh`, not `vh`: a collapsing mobile browser toolbar would otherwise hide the pagination
 * bar. Browsers without `dvh` drop the cap and scroll the document, which is acceptable.
 */
const TABLE_MAX_HEIGHT = 'max(280px, calc(100dvh - 420px))';

const typeOptions = ["SystemEvent", "SystemError", "UserEvent"];
const severityOptions = ["Info", "Warning", "Error", "Fatal"];
const manufacturerEventLanguage = "eng";

function displayNameFromDescriptions(
  descriptions: NumberRangeDescription[] | undefined,
  preferred: EventLanguageCode
): string {
  if (!Array.isArray(descriptions)) return "";
  const pref = descriptions.find((d) =>
    (d.language_code || "").toLowerCase().startsWith(preferred.toLowerCase())
  );
  const fromPref = (pref?.display_name || "").trim();
  if (fromPref) return fromPref;
  for (const d of descriptions) {
    const n = (d.display_name || "").trim();
    if (n) return n;
  }
  return "";
}

/** Numbered action lines for the home table; skips empty / whitespace-only steps. */
function formatActionStepsForTable(actions: unknown): string {
  if (!Array.isArray(actions)) return "";
  const nonempty = actions
    .map((s) => (typeof s === "string" ? s.trim() : String(s ?? "").trim()))
    .filter((s) => s.length > 0);
  return nonempty.map((step, index) => `${index + 1}. ${step}`).join("\n");
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editEventId, setEditEventId] = useState<number | null>(null);
  const [events, setEvents] = useState<FullEvent[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [language, setLanguage] = useState<EventLanguageCode>("ger");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const theme = useTheme();
  let [eventType, setEventType] = useState<string[]>(typeOptions);
  let [eventSeverity, setEventSeverity] = useState<string[]>(severityOptions);
  const [showResolvingEvents, setShowResolvingEvents] = useState(true);
  const [showManufacturerEvents, setShowManufacturerEvents] = useState(true);
  const [legacyEventsOnly, setLegacyEventsOnly] = useState(false);
  const { isReader, isAdmin } = getUserRoleFlags();
  let [filterMode, setFilterMode] = useState<"unapproved" | "commented" | "none">("none");
  const [nrBases, setNrBases] = useState<NumberRangeBaseFunction[]>([]);
  const [nrSubs, setNrSubs] = useState<NumberRangeSubFunction[]>([]);
  const [nrFilterBaseId, setNrFilterBaseId] = useState("");
  const [nrFilterSubId, setNrFilterSubId] = useState("");
  const [nrSubsLoading, setNrSubsLoading] = useState(false);

  const rowsPerPageRef = useRef(rowsPerPage);
  const searchRef = useRef("");
  const languageRef = useRef(language);
  const eventTypeRef = useRef(eventType);
  const eventSeverityRef = useRef(eventSeverity);
  const showResolvingEventsRef = useRef(showResolvingEvents);
  const showManufacturerEventsRef = useRef(showManufacturerEvents);
  const filterModeRef = useRef(filterMode);
  const nrFilterBaseIdRef = useRef("");
  const nrFilterSubIdRef = useRef("");
  const legacyEventsOnlyRef = useRef(false);
  /** When true, the filter `useEffect` skips one auto-fetch (clear already called `fetchEvents`). */
  const skipNextFilterAutoFetchRef = useRef(false);

  useEffect(() => { rowsPerPageRef.current = rowsPerPage; }, [rowsPerPage]);
  useEffect(() => { languageRef.current = language; }, [language]);
  useEffect(() => { eventTypeRef.current = eventType; }, [eventType]);
  useEffect(() => { eventSeverityRef.current = eventSeverity; }, [eventSeverity]);
  useEffect(() => { showResolvingEventsRef.current = showResolvingEvents; }, [showResolvingEvents]);
  useEffect(() => { showManufacturerEventsRef.current = showManufacturerEvents; }, [showManufacturerEvents]);
  useEffect(() => { filterModeRef.current = filterMode; }, [filterMode]);
  useEffect(() => {
    nrFilterBaseIdRef.current = nrFilterBaseId;
  }, [nrFilterBaseId]);
  useEffect(() => {
    nrFilterSubIdRef.current = nrFilterSubId;
  }, [nrFilterSubId]);
  useEffect(() => {
    legacyEventsOnlyRef.current = legacyEventsOnly;
  }, [legacyEventsOnly]);

  const { callApi } = useEventsApi();

  useEffect(() => {
    void (async () => {
      try {
        const data = await callApi(`${config.apiUrl}/api/number-range-base-function/`, "GET");
        const rows = Array.isArray(data?.results) ? (data.results as NumberRangeBaseFunction[]) : [];
        setNrBases(rows);
      } catch {
        setNrBases([]);
      }
    })();
  }, [callApi]);

  useEffect(() => {
    if (!nrFilterBaseId) {
      setNrSubs([]);
      setNrSubsLoading(false);
      return;
    }
    let cancelled = false;
    setNrSubs([]);
    setNrSubsLoading(true);
    void (async () => {
      try {
        const data = await callApi(
          `${config.apiUrl}/api/number-range-sub-function/?base=${encodeURIComponent(nrFilterBaseId)}`,
          "GET"
        );
        const rows = Array.isArray(data?.results) ? (data.results as NumberRangeSubFunction[]) : [];
        if (!cancelled) setNrSubs(rows);
      } catch {
        if (!cancelled) setNrSubs([]);
      } finally {
        if (!cancelled) setNrSubsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nrFilterBaseId, callApi]);

  const nrSubsWithDisplayName = useMemo(
    () =>
      nrSubs.filter((s) => displayNameFromDescriptions(s.descriptions, language).trim() !== ""),
    [nrSubs, language]
  );

  /** Same rule as AGV chips / Step1: bases with `show_on_agv === false` are omitted. */
  const nrBasesForHomeFilter = useMemo(
    () => nrBases.filter((b) => b.show_on_agv !== false),
    [nrBases]
  );

  useEffect(() => {
    if (!nrFilterBaseId) return;
    if (!nrBasesForHomeFilter.some((b) => String(b.id) === nrFilterBaseId)) {
      setNrFilterBaseId("");
      setNrFilterSubId("");
    }
  }, [nrBasesForHomeFilter, nrFilterBaseId]);

  useEffect(() => {
    if (!nrFilterSubId || nrSubsWithDisplayName.length === 0) return;
    if (!nrSubsWithDisplayName.some((s) => String(s.id) === nrFilterSubId)) {
      setNrFilterSubId("");
    }
  }, [nrSubsWithDisplayName, nrFilterSubId]);

  useEffect(() => {
    const raw = searchParams.get("open_event_id");
    if (!raw) return;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      setEditEventId(parsed);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open_event_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchEvents = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string,
      language: EventLanguageCode,
      eventType: string[],
      eventSeverity: string[],
      showResolvingEvents: boolean,
      showManufacturerEvents: boolean,
      filterMode: string
    ) => {
      try {
        const url = new URL(`${config.apiUrl}/api/events/`);
        url.searchParams.append("page", (page + 1).toString()); // Django beginnt bei 1

        if (pageSize !== -1) {
          url.searchParams.append("page_size", pageSize.toString());
        }

        const trimmedSearch = search.trim();
        if (trimmedSearch) {
          url.searchParams.append("search", trimmedSearch);
        }

        if (language) {
          url.searchParams.append("language", language);
        }

        if (eventType.length > 0) {
          url.searchParams.append("event_type", eventType.join(","));
        }

        if (eventSeverity.length > 0) {
          url.searchParams.append("event_severity", eventSeverity.join(","));
        }

        url.searchParams.append("resolving", showResolvingEvents ? "true" : "false");
        if (!showManufacturerEvents) {
          url.searchParams.append("manufacturer_events", "false");
        }
        if (legacyEventsOnlyRef.current) {
          url.searchParams.append("legacy_events_only", "true");
        }
        url.searchParams.append("admin_filter", filterMode);
        url.searchParams.append("latest", "true");

        const nrBaseTrim = nrFilterBaseIdRef.current.trim();
        if (nrBaseTrim) {
          url.searchParams.append("number_range_base_id", nrBaseTrim);
          const nrSubTrim = nrFilterSubIdRef.current.trim();
          if (nrSubTrim) {
            url.searchParams.append("number_range_sub_id", nrSubTrim);
          }
        }

        const data = await callApi(url.toString(), "GET");
        setEvents(data.results);
        setTotalCount(data.count);
        setHistorySuggestionCode(null);

        const normalizedSearch = trimmedSearch;
        const searchedCode = Number(normalizedSearch);
        const isCodeLookup = normalizedSearch !== "" && Number.isInteger(searchedCode);
        if (data.count === 0 && isCodeLookup) {
          try {
            const historyUrl = new URL(`${config.apiUrl}/api/events/history-chain/`);
            historyUrl.searchParams.set("event_code", String(searchedCode));
            const history = await callApi(historyUrl.toString(), "GET");
            if ((history?.count || 0) > 1) {
              setHistorySuggestionCode(searchedCode);
            }
          } catch {
            setHistorySuggestionCode(null);
          }
        }
      } catch (error) {
        console.error("Fehler beim Laden der Events:", error);
        setHistorySuggestionCode(null);
      }
    },
    [callApi]
  );

  const debouncedSearchFetch = useMemo(
    () =>
      debounce((searchValue: string) => {
        void fetchEvents(
          0,
          rowsPerPageRef.current,
          searchValue,
          languageRef.current,
          eventTypeRef.current,
          eventSeverityRef.current,
          showResolvingEventsRef.current,
          showManufacturerEventsRef.current,
          filterModeRef.current
        );
      }, 500),
    [fetchEvents]
  );

  useEffect(
    () => () => {
      debouncedSearchFetch.cancel();
    },
    [debouncedSearchFetch]
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    searchRef.current = value;
    setPage(0);
    debouncedSearchFetch(value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    searchRef.current = "";
    debouncedSearchFetch.cancel();
    setPage(0);
    void fetchEvents(
      0,
      rowsPerPageRef.current,
      "",
      language,
      eventType,
      eventSeverity,
      showResolvingEvents,
      showManufacturerEvents,
      filterMode
    );
  };

  useEffect(() => {
    if (skipNextFilterAutoFetchRef.current) {
      skipNextFilterAutoFetchRef.current = false;
      return;
    }
    debouncedSearchFetch.cancel();
    setPage(0);
    void fetchEvents(
      0,
      rowsPerPageRef.current,
      searchRef.current,
      language,
      eventType,
      eventSeverity,
      showResolvingEvents,
      showManufacturerEvents,
      filterMode
    );
  }, [
    eventType,
    eventSeverity,
    language,
    showResolvingEvents,
    showManufacturerEvents,
    filterMode,
    fetchEvents,
    debouncedSearchFetch,
    nrFilterBaseId,
    nrFilterSubId,
    legacyEventsOnly,
  ]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    void fetchEvents(
      newPage,
      rowsPerPage,
      searchRef.current,
      language,
      eventType,
      eventSeverity,
      showResolvingEvents,
      showManufacturerEvents,
      filterMode
    );
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    void fetchEvents(
      0,
      newSize,
      searchRef.current,
      language,
      eventType,
      eventSeverity,
      showResolvingEvents,
      showManufacturerEvents,
      filterMode
    );
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("error");
  const [historySuggestionCode, setHistorySuggestionCode] = useState<number | null>(null);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  const filterIsActive = (!typeOptions.every(item => eventType.includes(item))
    || !severityOptions.every(item => eventSeverity.includes(item))
    || !showResolvingEvents
    || !showManufacturerEvents
    || filterMode !== 'none'
    || nrFilterBaseId.trim() !== ""
    || legacyEventsOnly);

  const clearAllFilters = useCallback(() => {
    skipNextFilterAutoFetchRef.current = true;
    debouncedSearchFetch.cancel();
    searchRef.current = "";
    nrFilterBaseIdRef.current = "";
    nrFilterSubIdRef.current = "";
    legacyEventsOnlyRef.current = false;
    filterModeRef.current = "none";
    eventTypeRef.current = [...typeOptions];
    eventSeverityRef.current = [...severityOptions];
    showResolvingEventsRef.current = true;
    showManufacturerEventsRef.current = true;

    setSearchInput("");
    setNrFilterBaseId("");
    setNrFilterSubId("");
    setLegacyEventsOnly(false);
    setFilterMode("none");
    setEventType([...typeOptions]);
    setEventSeverity([...severityOptions]);
    setShowResolvingEvents(true);
    setShowManufacturerEvents(true);
    setPage(0);

    void fetchEvents(
      0,
      rowsPerPageRef.current,
      "",
      language,
      [...typeOptions],
      [...severityOptions],
      true,
      true,
      "none"
    );
  }, [debouncedSearchFetch, fetchEvents, language]);

  /*function ResetFilter() {
    eventType = typeOptions;
    eventSeverity = severityOptions;
    filterMode = 'none';
    debouncedFetchEvents();
  }*/

  return (
    // Deliberately uncapped: a height cap here does not scroll anything, it only lets the
    // children paint outside the space the document reserved for them — over the footer and
    // past the end of the app shell. The table container below is the one scrolling region.
    <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, margin: '2rem auto', mt: 2 }}>

      <Box sx={{ mb: '1rem' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',            // volle Breite des Containers
            gap: 2,                   // optionaler Abstand
            // Optional: Sticky Header (oben fixiert, scrollt mit Inhalt mit)
            // position: 'sticky',
            // top: 0,
            // zIndex: theme.zIndex.appBar, // falls nötig, damit es über Content bleibt
            // backgroundColor: theme.palette.background.default // optisch sauber
          }}
        >
          {/* Linke Seite: Home */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, mb: 0 }}>
              Home
            </Typography>
          </Box>

          {/* Mitte: Suche (zentriert) */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Gruppe für Suchfeld + Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Contract name | code | label"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                sx={{ width: 400, maxWidth: '100%', mr: 2 }} // << Abstand zum Icon
                color="primary"
                focused
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          size="small"
                          aria-label="Clear search"
                          onClick={handleClearSearch}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />

              {filterIsActive && (
                <Tooltip title="Search results may be incomplete due to an active filter!" arrow>
                  <WarningAmberIcon
                    sx={{
                      color: theme.palette.warning.main,
                      fontSize: 'xx-large',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
          {/* Rechts: Sprachumschaltung + Export */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={(_, newLang) => {
                if (newLang) {
                  setLanguage(newLang);
                }
              }}
              size="small"
              color="primary"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="ger">GER</ToggleButton>
              <ToggleButton value="eng">ENG</ToggleButton>
              <ToggleButton value="ita">ITA</ToggleButton>
              <ToggleButton value="cze">CZE</ToggleButton>
            </ToggleButtonGroup>

            <ExportMenu language={language} />
          </Box>
        </Box>

        {/* Alerts bleiben außerhalb der Leiste oder du packst sie darunter/oben je nach Bedarf */}
        <CustomAlert
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          message={alertMessage}
          severity={alertSeverity}
        />
      </Box>


      <Accordion expanded={expanded === 'filterOptions'} onChange={handleChange('filterOptions')} sx={{ mb: 1 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              width: "100%",
              mr: 1,
              my: 0.5,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, flexWrap: "wrap" }}>
            <Typography
              variant="body1"
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: theme.palette.text.secondary,
                flex: 1,
                minWidth: 0,
              }}
            >
              {filterIsActive && <FilterAltIcon sx={{ color: theme.palette.warning.main }} />}
              {!filterIsActive && <FilterAltIcon sx={{ color: theme.palette.primary.main }} />}
              Filter options…
            </Typography>
            <Tooltip title="Reset filters, number range, legacy mode, and search">
              <Button
                component="div"
                role="button"
                tabIndex={0}
                size="small"
                variant="outlined"
                startIcon={<FilterAltOffIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    clearAllFilters();
                  }
                }}
              >
                Clear all filters
              </Button>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" gap={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
            {/* Event Types */}
            {isAdmin && <FormControl component="fieldset" size="small" sx={{
              mb: 0,
              flexWrap: "wrap",
              alignItems: "center",
              border: "1px solid",
              borderColor: theme.palette.warning.main,
              borderRadius: 2,
              padding: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Event Types
              </FormLabel>
              <FormGroup row>
                {typeOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={eventType.includes(option)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newSelection = checked
                            ? [...eventType, option]
                            : eventType.filter((item) => item !== option);
                          setEventType(newSelection);
                        }}
                      />
                    }
                    label={option}
                    sx={{ color: theme.palette.text.primary }}
                  />
                ))}
              </FormGroup>
            </FormControl>}

            {/* Event Severities */}
            <FormControl component="fieldset" size="small"
              sx={{
                mb: 0,
                flexWrap: "wrap",
                alignItems: "center",
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                padding: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Event Severities
              </FormLabel>
              <FormGroup row>
                {severityOptions.map((option) => {
                  const isInfo = option === "Info";
                  return (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={eventSeverity.includes(option) || (isInfo && showResolvingEvents)}
                          disabled={isInfo && showResolvingEvents}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const newSelection = checked
                              ? [...eventSeverity, option]
                              : eventSeverity.filter((item) => item !== option);
                            setEventSeverity(newSelection);
                          }}
                        />
                      }
                      label={option}
                      sx={{ color: theme.palette.text.primary }}
                    />
                  )
                })}
              </FormGroup>
            </FormControl>

            <FormControl
              component="fieldset"
              size="small"
              sx={{
                mb: 0,
                flexWrap: "wrap",
                alignItems: "center",
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                padding: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Manufacturer events
              </FormLabel>
              <FormControlLabel
                sx={{ color: theme.palette.text.primary }}
                control={
                  <Checkbox
                    checked={showManufacturerEvents}
                    onChange={(e) => setShowManufacturerEvents(e.target.checked)}
                    size="small"
                  />
                }
                label="Show"
              />
            </FormControl>

            {/* Resolving Events */}
            {isAdmin && <FormControl component="fieldset" size="small" sx={{
              mb: 0,
              flexWrap: "wrap",
              alignItems: "center",
              border: "1px solid",
              borderColor: theme.palette.warning.main,
              borderRadius: 2,
              padding: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Resolving events
              </FormLabel>
              <FormControlLabel
                sx={{ color: theme.palette.text.primary }}
                control={
                  <Checkbox
                    checked={showResolvingEvents}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowResolvingEvents(checked);

                      if (checked && !eventSeverity.includes("Info")) {
                        setEventSeverity([...eventSeverity, "Info"]);
                      }
                    }}
                    size="small"
                  />
                }
                label="Show"
              />
            </FormControl>}
            <FormControl
              component="fieldset"
              size="small"
              sx={{
                mb: 0,
                flexWrap: "wrap",
                alignItems: "flex-start",
                maxWidth: 520,
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                padding: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Legacy events
              </FormLabel>
              <FormControlLabel
                sx={{ color: theme.palette.text.primary }}
                control={
                  <Checkbox
                    checked={legacyEventsOnly}
                    onChange={(e) => setLegacyEventsOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Only legacy events"
              />
              
            </FormControl>

            <FormControl
              component="fieldset"
              size="small"
              sx={{
                mb: 0,
                flexWrap: "wrap",
                alignItems: "flex-start",
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                padding: 1,
                backgroundColor: theme.palette.background.paper,
                minWidth: { xs: "100%", sm: 360 },
              }}
            >
              <FormLabel component="legend" sx={{ color: theme.palette.text.primary }}>
                Functions
              </FormLabel>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="home-nr-base-label">Base function</InputLabel>
                  <Select
                    labelId="home-nr-base-label"
                    label="Base function"
                    value={nrFilterBaseId}
                    onChange={(e: SelectChangeEvent) => {
                      setNrFilterBaseId(e.target.value);
                      setNrFilterSubId("");
                    }}
                  >
                    <MenuItem value="">
                      <em>Any base</em>
                    </MenuItem>
                    {[...nrBasesForHomeFilter]
                      .sort((a, b) => a.digit - b.digit || a.id - b.id)
                      .map((bf) => {
                        const nm = displayNameFromDescriptions(bf.descriptions, language);
                        return (
                          <MenuItem key={bf.id} value={String(bf.id)}>
                            {nm || `Base digit ${bf.digit}`}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }} disabled={!nrFilterBaseId}>
                  <InputLabel id="home-nr-sub-label">Sub-function</InputLabel>
                  <Select
                    labelId="home-nr-sub-label"
                    label="Sub-function"
                    value={nrFilterSubId}
                    onChange={(e: SelectChangeEvent) => setNrFilterSubId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All sub-functions</em>
                    </MenuItem>
                    {nrSubsLoading && (
                      <MenuItem disabled value="__nr_subs_loading__">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.25 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary" component="span">
                            Loading sub-functions…
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    {!nrSubsLoading &&
                      [...nrSubsWithDisplayName]
                        .sort((a, b) => a.digit - b.digit || a.id - b.id)
                        .map((sf) => (
                          <MenuItem key={sf.id} value={String(sf.id)}>
                            {displayNameFromDescriptions(sf.descriptions, language)}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </Stack>
            </FormControl>

            <AdminFilter
              isReader={isReader}
              value={filterMode}
              onChange={setFilterMode}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {events.length === 0 && historySuggestionCode !== null && (
        <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              p: 1.25,
              display: "inline-flex",
              maxWidth: "fit-content",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "info.light",
              backgroundColor: (t) => (t.palette.mode === "dark" ? "rgba(2,136,209,0.12)" : "rgba(2,136,209,0.08)"),
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
              color: "text.primary",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <InfoOutlinedIcon fontSize="small" color="info" />
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                No active result for {historySuggestionCode}. History available.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              color="info"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(`/event-history?event_code=${historySuggestionCode}`)}
            >
              Open history explorer
            </Button>
          </Box>
        </Box>
      )}
      {events.length === 0 && historySuggestionCode === null && <EmtpyState />}
      {/* Tabelle mit Scrollbar */}
      {/*expanded !== 'filterOptions' && */<TableContainer
        component={Paper}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          // The cap belongs on this element, the one that actually scrolls.
          maxHeight: TABLE_MAX_HEIGHT,
          maxWidth: '100%',
        }}
      >

        {events.length > 0 && <Table sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                minWidth: 75, maxWidth: 120,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}><Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                  {legacyEventsOnly ? "Legacy code" : "Event code"}
                </Typography></TableCell>
              <TableCell sx={{
                minWidth: 120,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}><Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                  Severity
                </Typography></TableCell>
              <TableCell sx={{
                minWidth: 120,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}><Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                  Label
                </Typography></TableCell>
              <TableCell sx={{
                minWidth: 120,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}><Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                  Description
                </Typography></TableCell>
              <TableCell sx={{
                minWidth: 120,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}><Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                  Action
                </Typography></TableCell>
              <TableCell sx={{
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 10,
              }} />
            </TableRow>
          </TableHead>

          <TableBody >
            {events.map((event) => (
              <TableRow hover role="checkbox" key={event.global_event_id}
                sx={(theme) => ({
                  backgroundColor:
                    event.approved_by == null || event.approved_at == null
                      ? alpha(theme.palette.warning.main, 0.25) // 25% Deckkraft
                      : 'inherit',
                })}

              >
                <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setEditEventId(event.global_event_id);
                  }}
                >
                  {legacyEventsOnly
                    ? (event.attributes?.event_code_old ?? "—")
                    : event.event_code}
                </TableCell>
                <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }}>{event.attributes?.severity}</TableCell>
                {!event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }}>{getDescriptionByLanguage(event, language)?.label}</TableCell>}
                {event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }}>{getDescriptionByLanguage(event, manufacturerEventLanguage)?.label}</TableCell>}
                {!event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }}>{getDescriptionByLanguage(event, language)?.description}</TableCell>}
                {event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }}>{getDescriptionByLanguage(event, manufacturerEventLanguage)?.description}</TableCell>}
                {!event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }} style={{ whiteSpace: 'pre-line' }}>
                  {formatActionStepsForTable(getDescriptionByLanguage(event, language)?.actions)}
                </TableCell>}
                {event.attributes?.manufacturer_event && <TableCell
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => { setEditEventId(event.global_event_id) }} style={{ whiteSpace: 'pre-line' }}>
                  {formatActionStepsForTable(getDescriptionByLanguage(event, manufacturerEventLanguage)?.actions)}
                </TableCell>}
                <TableCell
                  sx={{
                    position: 'sticky',
                    right: 0,
                    zIndex: 1,
                    minWidth: 60,
                  }}
                >
                  <Box sx={{ zIndex: 7 }} display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                    <EventMenuButton
                      event={event}
                      setEditEventId={setEditEventId}
                      onClose={() =>
                        void fetchEvents(
                          page,
                          rowsPerPage,
                          searchRef.current,
                          language,
                          eventType,
                          eventSeverity,
                          showResolvingEvents,
                          showManufacturerEvents,
                          filterMode
                        )}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>}
        <EventsFormDialog
          open={!!editEventId}
          onClose={() => {
            setEditEventId(null);
            void fetchEvents(
              page,
              rowsPerPage,
              searchRef.current,
              language,
              eventType,
              eventSeverity,
              showResolvingEvents,
              showManufacturerEvents,
              filterMode
            );
          }}
          onSaved={({ eventCode, approved }) => {
            void fetchEvents(
              page,
              rowsPerPage,
              searchRef.current,
              language,
              eventType,
              eventSeverity,
              showResolvingEvents,
              showManufacturerEvents,
              filterMode
            );
            if (approved)
              setAlertMessage(`Successfully approved event ${eventCode}!`);
            else
              setAlertMessage(`Successfully saved ${eventCode ?? 'event'}.`);
            setAlertSeverity("success");
            setAlertOpen(true);
          }}
          eventId={editEventId}
          createNewEvent={false}
          onNavigateToEvent={(id) => setEditEventId(id)}
        />
        <CustomAlert
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          message={alertMessage}
          severity={alertSeverity}
        />
      </TableContainer>}
      {/* Pagination bar, outside the table's scroll area so it stays put while the rows
          scroll. */}
      {events.length > 0 && <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          zIndex: 10,
          width: '100%',
        }}
      >
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 25, 50]}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelDisplayedRows={({ page }) => {
            const totalPages = rowsPerPage > 0 ? Math.ceil(totalCount / rowsPerPage) : 0;
            const currentPage = totalPages === 0 ? 0 : page + 1;
            return `Page ${currentPage} of ${totalPages}`;
          }}
          slotProps={{
            select: {
              inputProps: {
                'aria-label': 'Rows per page',
              },
              native: true,
            },
          }}
          sx={{
            width: '100%',
            px: 2,
            py: 1,
          }}
        />
      </Box>}
    </Box>);
};

export default HomePage;